import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { STORAGE_URL } from '../../api/axios';
import { FiPlus, FiEdit2, FiTrash2, FiPackage, FiChevronDown, FiChevronUp, FiX, FiArrowLeft, FiImage, FiUploadCloud, FiStar, FiSearch } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

type ImageItem = { type: 'file'; file: File; preview: string } | { type: 'existing'; url: string; path: string };

export default function Products() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [deliveryAreas, setDeliveryAreas] = useState<any[]>([]);
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [tagInput, setTagInput] = useState('');
  const [tagInputAr, setTagInputAr] = useState('');
  const [images, setImages] = useState<ImageItem[]>([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [draggingOver, setDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [removedExisting, setRemovedExisting] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [prodPage, setProdPage] = useState(1);
  const [prodLastPage, setProdLastPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const searchTimer = useRef<any>(null);

  const [form, setForm] = useState({
    category_id: '', name: '', name_ar: '', description: '', description_ar: '',
    price: '', compare_price: '', sku: '', is_active: true, is_featured: false,
    sort_order: 0, calories: '', preparation_time: '',
    delivery_area_ids: [] as number[],
    model_number: '', stock: '', track_stock: false, subtract_stock: true,
    date_available: '', requires_delivery: false,
    discount_type: '' as '' | 'fixed' | 'percentage',
    discount_value: '', discount_start: '', discount_end: '', discount_quantity: '',
    meta_title: '', meta_description: '', meta_keywords: '',
    tags: [] as string[],
    tags_ar: [] as string[],
    attributes: [] as { key: string; value: string }[],
    option_groups: [] as {
      name: string; name_ar: string; type: string; required: boolean;
      min_selections: number; max_selections: number; allow_quantity: boolean;
      choices: { name: string; name_ar: string; price: string; max_qty: number }[];
    }[],
  });

  useEffect(() => {
    if (store?.id) { loadProducts(); loadCategories(); loadDeliveryAreas(); }
  }, [store?.id]);

  useEffect(() => {
    if (store?.id && prodPage > 1) loadProducts(prodPage);
  }, [prodPage]);

  useEffect(() => {
    if (store?.id) { setProdPage(1); loadProducts(1); }
  }, [filterCategory, filterStatus]);

  const handleSearch = (val: string) => {
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setProdPage(1); loadProducts(1); }, 400);
  };

  const loadProducts = async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('per_page', '20');
      params.set('page', String(pg));
      if (searchQuery) params.set('search', searchQuery);
      if (filterCategory) params.set('category_id', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/stores/${store.id}/products?${params}`);
      const data = res.data;
      if (data.data) {
        if (pg === 1) setProducts(data.data);
        else setProducts((prev) => [...prev, ...data.data]);
        setProdLastPage(data.last_page || 1);
      } else {
        setProducts(data);
      }
    } finally { setLoading(false); }
  };
  const loadCategories = async () => { const res = await api.get(`/stores/${store.id}/categories`); setCategories(res.data); };
  const loadDeliveryAreas = async () => { try { const res = await api.get(`/stores/${store.id}/delivery-areas`); setDeliveryAreas(res.data); } catch {} };

  const toggleSection = (key: string) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  const openForm = (product?: any) => {
    if (product) {
      setEditing(product);
      setForm({
        category_id: product.category_id, name: product.name, name_ar: product.name_ar || '',
        description: product.description || '', description_ar: product.description_ar || '',
        price: product.price, compare_price: product.compare_price || '',
        sku: product.sku || '', is_active: product.is_active, is_featured: product.is_featured,
        sort_order: product.sort_order ?? 0, calories: product.calories || '', preparation_time: product.preparation_time || '',
        delivery_area_ids: product.delivery_area_ids || [],
        model_number: product.model_number || '', stock: product.stock ?? '',
        track_stock: product.track_stock || false, subtract_stock: product.subtract_stock ?? true,
        date_available: product.date_available ? product.date_available.split('T')[0] : '',
        requires_delivery: product.requires_delivery || false,
        discount_type: product.discount_type || '',
        discount_value: product.discount_value || '',
        discount_start: product.discount_start ? product.discount_start.slice(0, 16) : '',
        discount_end: product.discount_end ? product.discount_end.slice(0, 16) : '',
        discount_quantity: product.discount_quantity ?? '',
        meta_title: product.meta_title || '', meta_description: product.meta_description || '',
        meta_keywords: product.meta_keywords || '',
        tags: product.tags || [], tags_ar: product.tags_ar || [], attributes: product.attributes || [], option_groups: product.option_groups || [],
      });
      const imgs: ImageItem[] = [];
      if (product.image) imgs.push({ type: 'existing', url: `${STORAGE_URL}/${product.image}`, path: product.image });
      if (product.additional_images) {
        product.additional_images.forEach((img: string) => {
          imgs.push({ type: 'existing', url: `${STORAGE_URL}/${img}`, path: img });
        });
      }
      setImages(imgs);
      setMainImageIndex(0);
    } else {
      setEditing(null);
      setForm({
        category_id: categories[0]?.id || '', name: '', name_ar: '', description: '', description_ar: '',
        price: '', compare_price: '', sku: '', is_active: true, is_featured: false,
        sort_order: 0, calories: '', preparation_time: '', delivery_area_ids: [],
        model_number: '', stock: '', track_stock: false, subtract_stock: true,
        date_available: '', requires_delivery: false,
        discount_type: '', discount_value: '', discount_start: '', discount_end: '', discount_quantity: '',
        meta_title: '', meta_description: '', meta_keywords: '',
        tags: [], tags_ar: [], attributes: [], option_groups: [],
      });
      setImages([]);
      setMainImageIndex(0);
    }
    setRemovedExisting([]);
    setExpandedSections({});
    setTagInput('');
    setView('form');
  };

  const goBack = () => { setView('list'); setEditing(null); cleanupPreviews(); };

  const cleanupPreviews = () => {
    images.forEach(img => { if (img.type === 'file') URL.revokeObjectURL(img.preview); });
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const newImages: ImageItem[] = Array.from(files)
      .filter(f => f.type.startsWith('image/'))
      .map(f => ({ type: 'file' as const, file: f, preview: URL.createObjectURL(f) }));
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const removeImage = (index: number) => {
    setImages(prev => {
      const img = prev[index];
      if (img.type === 'file') URL.revokeObjectURL(img.preview);
      if (img.type === 'existing') setRemovedExisting(r => [...r, img.path]);
      const next = prev.filter((_, i) => i !== index);
      if (mainImageIndex === index) setMainImageIndex(0);
      else if (mainImageIndex > index) setMainImageIndex(m => m - 1);
      return next;
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setDraggingOver(true); }, []);
  const handleDragLeave = useCallback(() => setDraggingOver(false), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key === 'option_groups') {
        const groups = (Array.isArray(val) ? val : []).map((g: any) => ({
          ...g,
          min_selections: g.min_selections === '' || g.min_selections == null || Number.isNaN(Number(g.min_selections))
            ? 0
            : Number(g.min_selections),
          max_selections: g.max_selections === '' || g.max_selections == null || Number.isNaN(Number(g.max_selections))
            ? 0
            : Number(g.max_selections),
          choices: (g.choices || []).map((c: any) => ({
            ...c,
            max_qty: c.max_qty === '' || c.max_qty == null || Number.isNaN(Number(c.max_qty))
              ? 1
              : Number(c.max_qty),
          })),
        }));
        formData.append(key, JSON.stringify(groups));
      } else if (['delivery_area_ids', 'tags', 'tags_ar', 'attributes'].includes(key)) {
        formData.append(key, JSON.stringify(val));
      } else if (key === 'sort_order') {
        const n = val === '' || val === null || val === undefined ? 0 : Number(val);
        formData.append(key, String(Number.isFinite(n) ? n : 0));
      } else if (val !== '' && val !== null && val !== undefined) {
        formData.append(key, typeof val === 'boolean' ? (val ? '1' : '0') : String(val));
      }
    });

    const mainImg = images[mainImageIndex];
    if (mainImg?.type === 'file') formData.append('image', mainImg.file);

    images.forEach((img, i) => {
      if (i !== mainImageIndex && img.type === 'file') {
        formData.append('additional_images[]', img.file);
      }
    });

    if (removedExisting.length) formData.append('remove_additional_images', JSON.stringify(removedExisting));

    try {
      if (editing) {
        formData.append('_method', 'PUT');
        await api.post(`/stores/${store.id}/products/${editing.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(dt('product_updated', lang));
      } else {
        await api.post(`/stores/${store.id}/products`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(dt('product_created', lang));
      }
      cleanupPreviews();
      setView('list');
      loadProducts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving_product', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(dt('delete_product_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/products/${id}`);
      toast.success(dt('product_deleted', lang));
      loadProducts();
    } catch { toast.error(dt('error_deleting_product', lang)); }
  };

  const toggleMulti = (arr: number[], id: number) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
  const addTag = () => { const tag = tagInput.trim(); if (tag && !form.tags.includes(tag)) setForm({ ...form, tags: [...form.tags, tag] }); setTagInput(''); };
  const removeTag = (tag: string) => setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  const addTagAr = () => { const tag = tagInputAr.trim(); if (tag && !form.tags_ar.includes(tag)) setForm({ ...form, tags_ar: [...form.tags_ar, tag] }); setTagInputAr(''); };
  const removeTagAr = (tag: string) => setForm({ ...form, tags_ar: form.tags_ar.filter(t => t !== tag) });
  const addAttribute = () => setForm({ ...form, attributes: [...form.attributes, { key: '', value: '' }] });
  const removeAttribute = (i: number) => setForm({ ...form, attributes: form.attributes.filter((_, idx) => idx !== i) });
  const updateAttribute = (i: number, field: 'key' | 'value', val: string) => { const a = [...form.attributes]; a[i] = { ...a[i], [field]: val }; setForm({ ...form, attributes: a }); };
  const addOptionGroup = () => setForm({
    ...form, option_groups: [...form.option_groups, {
      name: '', name_ar: '', type: 'radio', required: false,
      min_selections: 0, max_selections: 1, allow_quantity: false,
      choices: [{ name: '', name_ar: '', price: '', max_qty: 1 }],
    }]
  });
  const removeOptionGroup = (i: number) => setForm({ ...form, option_groups: form.option_groups.filter((_, idx) => idx !== i) });
  const updateOptionGroup = (i: number, field: string, val: any) => {
    const g = [...form.option_groups]; g[i] = { ...g[i], [field]: val }; setForm({ ...form, option_groups: g });
  };
  const addChoice = (gi: number) => {
    const g = [...form.option_groups];
    g[gi] = { ...g[gi], choices: [...g[gi].choices, { name: '', name_ar: '', price: '', max_qty: 1 }] };
    setForm({ ...form, option_groups: g });
  };
  const removeChoice = (gi: number, ci: number) => {
    const g = [...form.option_groups];
    g[gi] = { ...g[gi], choices: g[gi].choices.filter((_, idx) => idx !== ci) };
    setForm({ ...form, option_groups: g });
  };
  const updateChoice = (gi: number, ci: number, field: string, val: any) => {
    const g = [...form.option_groups];
    const choices = [...g[gi].choices];
    choices[ci] = { ...choices[ci], [field]: val };
    g[gi] = { ...g[gi], choices };
    setForm({ ...form, option_groups: g });
  };

  if (!store) return null;

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  const sectionHeader = (key: string, label: string) => (
    <div onClick={() => toggleSection(key)} className="pf-section-header">
      <strong>{label}</strong>
      {expandedSections[key] ? <FiChevronUp /> : <FiChevronDown />}
    </div>
  );

  // ==================== FORM VIEW ====================
  if (view === 'form') {
    return (
      <div className="pf-page">
        <div className="pf-topbar">
          <button type="button" className="btn btn-white" onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiArrowLeft /> {t('Back', 'رجوع')}
          </button>
          <h2 className="pf-title">{editing ? dt('edit_product', lang) : dt('add_product', lang)}</h2>
          <button type="submit" form="product-form" className="btn btn-primary" disabled={saving}>
            {saving ? t('Saving...', 'جاري الحفظ...') : editing ? dt('update', lang) : dt('create', lang)}
          </button>
        </div>

        <form id="product-form" onSubmit={handleSubmit} className="pf-layout">
          {/* Images */}
            <div className="pf-card">
              <h3 className="pf-card-title"><FiImage /> {t('Product Images', 'صور المنتج')}</h3>

              {/* Dropzone */}
              <div
                className={`pf-dropzone ${draggingOver ? 'pf-dropzone-active' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <FiUploadCloud size={32} />
                <p>{t('Drag & drop images here', 'اسحب وأفلت الصور هنا')}</p>
                <span>{t('or click to browse', 'أو انقر للتصفح')}</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
                />
              </div>

              {/* Image Grid */}
              {images.length > 0 && (
                <div className="pf-image-grid">
                  {images.map((img, i) => (
                    <div key={i} className={`pf-image-item ${i === mainImageIndex ? 'pf-image-main' : ''}`}>
                      <img src={img.type === 'file' ? img.preview : img.url} alt="" />
                      <div className="pf-image-actions">
                        {i !== mainImageIndex && (
                          <button type="button" title={t('Set as main', 'تعيين كرئيسية')} onClick={() => setMainImageIndex(i)} className="pf-img-btn">
                            <FiStar />
                          </button>
                        )}
                        <button type="button" title={t('Remove', 'إزالة')} onClick={() => removeImage(i)} className="pf-img-btn pf-img-btn-danger">
                          <FiX />
                        </button>
                      </div>
                      {i === mainImageIndex && <div className="pf-main-badge">{t('Main', 'رئيسية')}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="pf-card">
              <h3 className="pf-card-title">{t('Basic Information', 'المعلومات الأساسية')}</h3>
              <div className="form-group">
                <label className="form-label">{dt('category', lang)} *</label>
                <select className="form-input" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} required>
                  <option value="">{dt('select_category', lang)}</option>
                  {categories.map(c => (
                    <React.Fragment key={c.id}>
                      <option value={c.id}>{c.name}</option>
                      {c.children?.map((sub: any) => (
                        <option key={sub.id} value={sub.id}>&nbsp;&nbsp;{lang === 'ar' ? '↲' : '↳'} {sub.name}</option>
                      ))}
                    </React.Fragment>
                  ))}
                </select>
              </div>
              <div className="pf-grid-2">
                <div className="form-group">
                  <label className="form-label">{dt('name_en', lang)} *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('name_ar', lang)}</label>
                  <input className="form-input" value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{dt('desc_en', lang)}</label>
                <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{dt('desc_ar', lang)}</label>
                <textarea className="form-input" rows={3} value={form.description_ar} onChange={e => setForm({ ...form, description_ar: e.target.value })} dir="rtl" />
              </div>
            </div>

            {/* Pricing */}
            <div className="pf-card">
              <h3 className="pf-card-title">{t('Pricing & Inventory', 'التسعير والمخزون')}</h3>
              <div className="pf-grid-2">
                <div className="form-group">
                  <label className="form-label">{dt('price', lang)} ({store.currency_symbol}) *</label>
                  <input type="number" step="0.001" className="form-input" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('compare_price', lang)}</label>
                  <input type="number" step="0.001" className="form-input" value={form.compare_price} onChange={e => setForm({ ...form, compare_price: e.target.value })} />
                </div>
              </div>
              <div className="pf-grid-3">
                <div className="form-group">
                  <label className="form-label">{dt('sku', lang)}</label>
                  <input className="form-input" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('model_number', lang)}</label>
                  <input className="form-input" value={form.model_number} onChange={e => setForm({ ...form, model_number: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('stock', lang)}</label>
                  <input type="number" className="form-input" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} />
                </div>
              </div>
              <div className="pf-toggles">
                <label className="pf-toggle"><input type="checkbox" checked={form.track_stock as boolean} onChange={e => setForm({ ...form, track_stock: e.target.checked })} /> {dt('track_stock', lang)}</label>
                <label className="pf-toggle"><input type="checkbox" checked={form.subtract_stock as boolean} onChange={e => setForm({ ...form, subtract_stock: e.target.checked })} /> {dt('subtract_stock', lang)}</label>
              </div>
            </div>

            {/* Discount / Sale */}
            <div className="pf-card">
              <div className="pf-section-toggle" onClick={() => setExpandedSections(s => ({ ...s, discount: !s.discount }))}>
                <h3 className="pf-card-title" style={{ margin: 0 }}>{t('Discount / Sale', 'خصم / تخفيض')}</h3>
                {expandedSections.discount ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.discount && (
                <div style={{ marginTop: 16 }}>
                  <div className="pf-grid-2">
                    <div className="form-group">
                      <label className="form-label">{t('Discount Type', 'نوع الخصم')}</label>
                      <select className="form-input" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value as any })}>
                        <option value="">{t('No Discount', 'بدون خصم')}</option>
                        <option value="fixed">{t('Fixed Amount', 'مبلغ ثابت')} ({store.currency_symbol})</option>
                        <option value="percentage">{t('Percentage', 'نسبة مئوية')} (%)</option>
                      </select>
                    </div>
                    {form.discount_type && (
                      <div className="form-group">
                        <label className="form-label">
                          {form.discount_type === 'percentage' ? t('Discount %', 'نسبة الخصم %') : `${t('Discount Amount', 'مبلغ الخصم')} (${store.currency_symbol})`}
                        </label>
                        <input
                          type="number" step={form.discount_type === 'percentage' ? '1' : '0.001'}
                          min="0" max={form.discount_type === 'percentage' ? '100' : undefined}
                          className="form-input" value={form.discount_value}
                          onChange={e => setForm({ ...form, discount_value: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                  {form.discount_type && (
                    <>
                      {form.price && form.discount_value && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', marginBottom: 12, fontSize: 13 }}>
                          <strong>{t('Sale Price', 'سعر البيع')}: </strong>
                          {store.currency_symbol}{' '}
                          {form.discount_type === 'percentage'
                            ? (Number(form.price) * (1 - Number(form.discount_value) / 100)).toFixed(3)
                            : Math.max(0, Number(form.price) - Number(form.discount_value)).toFixed(3)}
                          <span style={{ marginInlineStart: 8, textDecoration: 'line-through', color: '#999' }}>
                            {store.currency_symbol} {Number(form.price).toFixed(3)}
                          </span>
                        </div>
                      )}
                      <div className="pf-grid-2">
                        <div className="form-group">
                          <label className="form-label">{t('Start Date & Time', 'تاريخ البداية')}</label>
                          <input type="datetime-local" className="form-input" value={form.discount_start} onChange={e => setForm({ ...form, discount_start: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{t('End Date & Time', 'تاريخ النهاية')}</label>
                          <input type="datetime-local" className="form-input" value={form.discount_end} onChange={e => setForm({ ...form, discount_end: e.target.value })} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('Limited Quantity (0 = unlimited)', 'كمية محدودة (0 = غير محدود)')}</label>
                        <input type="number" min="0" className="form-input" value={form.discount_quantity} onChange={e => setForm({ ...form, discount_quantity: e.target.value })} placeholder={t('Leave empty for unlimited', 'اتركه فارغاً للكمية غير المحدودة')} />
                      </div>
                      {editing?.discount_used > 0 && (
                        <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
                          {t('Used', 'مستخدم')}: {editing.discount_used} / {editing.discount_quantity || '∞'}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Status & Details */}
            <div className="pf-card">
              <h3 className="pf-card-title">{t('Status & Details', 'الحالة والتفاصيل')}</h3>
              <div className="pf-grid-2">
                <div className="form-group">
                  <label className="form-label">{dt('calories', lang)}</label>
                  <input type="number" className="form-input" value={form.calories} onChange={e => setForm({ ...form, calories: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('prep_time', lang)}</label>
                  <input className="form-input" placeholder={lang === 'ar' ? '15 دقيقة' : '15 min'} value={form.preparation_time} onChange={e => setForm({ ...form, preparation_time: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{dt('date_available', lang)}</label>
                <div className="pf-date-wrapper" onClick={() => dateInputRef.current?.showPicker?.()}>
                  <input
                    ref={dateInputRef}
                    type="date"
                    className="pf-date-hidden"
                    value={form.date_available}
                    onChange={e => setForm({ ...form, date_available: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="YYYY-MM-DD"
                    value={form.date_available}
                    readOnly
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className="pf-toggles">
                <label className="pf-toggle"><input type="checkbox" checked={form.is_active as boolean} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> {dt('active', lang)}</label>
                <label className="pf-toggle"><input type="checkbox" checked={form.is_featured as boolean} onChange={e => setForm({ ...form, is_featured: e.target.checked })} /> {dt('featured', lang)}</label>
                <label className="pf-toggle"><input type="checkbox" checked={form.requires_delivery as boolean} onChange={e => setForm({ ...form, requires_delivery: e.target.checked })} /> {dt('requires_delivery', lang)}</label>
              </div>
            </div>

            {/* Tags */}
            <div className="pf-card">
              {sectionHeader('tags', dt('tags', lang))}
              {expandedSections.tags && (
                <div style={{ marginTop: 12 }}>
                  <label className="form-label">{lang === 'ar' ? 'الوسوم (إنجليزي)' : 'Tags (English)'}</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input className="form-input" placeholder={dt('add_tag', lang)} value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} style={{ flex: 1 }} />
                    <button type="button" className="btn btn-sm btn-white" onClick={addTag}><FiPlus /></button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {form.tags.map(tag => (
                      <span key={tag} className="pf-tag">{tag} <FiX style={{ cursor: 'pointer', fontSize: 12 }} onClick={() => removeTag(tag)} /></span>
                    ))}
                  </div>
                  <label className="form-label" style={{ marginTop: 14, display: 'block' }}>{lang === 'ar' ? 'الوسوم (عربي)' : 'Tags (Arabic)'}</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input className="form-input" placeholder={lang === 'ar' ? 'أضف وسمًا' : 'Add Arabic tag'} value={tagInputAr} onChange={e => setTagInputAr(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTagAr(); } }} dir="rtl" style={{ flex: 1 }} />
                    <button type="button" className="btn btn-sm btn-white" onClick={addTagAr}><FiPlus /></button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {form.tags_ar.map(tag => (
                      <span key={tag} className="pf-tag" dir="rtl">{tag} <FiX style={{ cursor: 'pointer', fontSize: 12 }} onClick={() => removeTagAr(tag)} /></span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Attributes */}
            <div className="pf-card">
              {sectionHeader('attributes', dt('attributes', lang))}
              {expandedSections.attributes && (
                <div style={{ marginTop: 12 }}>
                  {form.attributes.map((attr, i) => (
                    <div key={i} className="pf-grid-attr">
                      <input className="form-input" placeholder={dt('attribute_key', lang)} value={attr.key} onChange={e => updateAttribute(i, 'key', e.target.value)} />
                      <input className="form-input" placeholder={dt('attribute_value', lang)} value={attr.value} onChange={e => updateAttribute(i, 'value', e.target.value)} />
                      <button type="button" className="btn btn-sm btn-white" onClick={() => removeAttribute(i)} style={{ color: 'var(--danger)' }}><FiTrash2 /></button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-white" onClick={addAttribute}><FiPlus /> {dt('add_attribute', lang)}</button>
                </div>
              )}
            </div>

            {/* Option Groups */}
            <div className="pf-card">
              {sectionHeader('options', t('Option Groups', 'مجموعات الخيارات'))}
              {expandedSections.options && (
                <div style={{ marginTop: 12 }}>
                  {form.option_groups.map((group, gi) => (
                    <div key={gi} style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 16, marginBottom: 12, background: 'var(--gray-50)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <strong style={{ fontSize: 13 }}>{t('Group', 'مجموعة')} {gi + 1}</strong>
                        <button type="button" className="btn btn-sm btn-white" onClick={() => removeOptionGroup(gi)} style={{ color: 'var(--danger)' }}><FiTrash2 /></button>
                      </div>
                      <div className="pf-grid-2">
                        <div className="form-group">
                          <label className="form-label">{t('Group Name (EN)', 'اسم المجموعة (EN)')}</label>
                          <input className="form-input" value={group.name} onChange={e => updateOptionGroup(gi, 'name', e.target.value)} placeholder="e.g. Bread Type" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{t('Group Name (AR)', 'اسم المجموعة (AR)')}</label>
                          <input className="form-input" value={group.name_ar} onChange={e => updateOptionGroup(gi, 'name_ar', e.target.value)} dir="rtl" placeholder="مثال: نوع الخبز" />
                        </div>
                      </div>
                      <div className="pf-grid-3">
                        <div className="form-group">
                          <label className="form-label">{t('Type', 'النوع')}</label>
                          <select className="form-input" value={group.type} onChange={e => updateOptionGroup(gi, 'type', e.target.value)}>
                            <option value="radio">{t('Radio (pick one)', 'اختيار واحد')}</option>
                            <option value="checkbox">{t('Checkbox (pick many)', 'اختيار متعدد')}</option>
                            <option value="select">{t('Select dropdown', 'قائمة منسدلة')}</option>
                            <option value="text">{t('Text input', 'إدخال نص')}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">{t('Min selections', 'أدنى اختيار')}</label>
                          <input
                            type="number"
                            className="form-input"
                            min="0"
                            value={group.min_selections}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateOptionGroup(
                                gi,
                                'min_selections',
                                raw === '' ? '' : (Number.isNaN(parseInt(raw, 10)) ? '' : parseInt(raw, 10)),
                              );
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{t('Max selections', 'أقصى اختيار')}</label>
                          <input
                            type="number"
                            className="form-input"
                            min="0"
                            value={group.max_selections}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateOptionGroup(
                                gi,
                                'max_selections',
                                raw === '' ? '' : (Number.isNaN(parseInt(raw, 10)) ? '' : parseInt(raw, 10)),
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="pf-toggles" style={{ marginBottom: 12 }}>
                        <label className="pf-toggle">
                          <input type="checkbox" checked={group.required} onChange={e => updateOptionGroup(gi, 'required', e.target.checked)} />
                          {t('Required', 'مطلوب')}
                        </label>
                        <label className="pf-toggle">
                          <input type="checkbox" checked={group.allow_quantity} onChange={e => updateOptionGroup(gi, 'allow_quantity', e.target.checked)} />
                          {t('Allow quantity per choice', 'السماح بالكمية لكل خيار')}
                        </label>
                      </div>

                      {/* Choices */}
                      <div style={{ marginTop: 8 }}>
                        <label className="form-label" style={{ fontSize: 12 }}>{t('Choices', 'الخيارات')}</label>
                        {group.choices.map((choice, ci) => (
                          <div key={ci} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 60px auto', gap: 6, marginBottom: 6 }}>
                            <input className="form-input" placeholder={t('Name EN', 'الاسم EN')} value={choice.name} onChange={e => updateChoice(gi, ci, 'name', e.target.value)} style={{ fontSize: 13, padding: '8px 10px' }} />
                            <input className="form-input" placeholder={t('Name AR', 'الاسم AR')} value={choice.name_ar} onChange={e => updateChoice(gi, ci, 'name_ar', e.target.value)} dir="rtl" style={{ fontSize: 13, padding: '8px 10px' }} />
                            <input type="number" step="0.001" className="form-input" placeholder={t('Price', 'السعر')} value={choice.price} onChange={e => updateChoice(gi, ci, 'price', e.target.value)} style={{ fontSize: 13, padding: '8px 10px' }} />
                            {group.allow_quantity && (
                              <input
                                type="number"
                                className="form-input"
                                placeholder={t('Max Q', 'أقصى ك')}
                                value={choice.max_qty}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  updateChoice(
                                    gi,
                                    ci,
                                    'max_qty',
                                    raw === '' ? '' : (Number.isNaN(parseInt(raw, 10)) ? '' : parseInt(raw, 10)),
                                  );
                                }}
                                style={{ fontSize: 13, padding: '8px 10px' }}
                              />
                            )}
                            <button type="button" className="btn btn-sm btn-white" onClick={() => removeChoice(gi, ci)} style={{ color: 'var(--danger)', padding: '6px' }}><FiX size={14} /></button>
                          </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-white" onClick={() => addChoice(gi)} style={{ fontSize: 12 }}>
                          <FiPlus size={12} /> {t('Add Choice', 'إضافة خيار')}
                        </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-white" onClick={addOptionGroup}>
                    <FiPlus /> {t('Add Option Group', 'إضافة مجموعة خيارات')}
                  </button>
                </div>
              )}
            </div>

            {/* SEO */}
            <div className="pf-card">
              {sectionHeader('seo', dt('seo_settings', lang))}
              {expandedSections.seo && (
                <div style={{ marginTop: 12 }}>
                  <div className="form-group">
                    <label className="form-label">{dt('meta_title', lang)}</label>
                    <input className="form-input" value={form.meta_title} onChange={e => setForm({ ...form, meta_title: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{dt('meta_description', lang)}</label>
                    <textarea className="form-input" rows={2} value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{dt('meta_keywords', lang)}</label>
                    <input className="form-input" placeholder={lang === 'ar' ? 'كلمة1، كلمة2' : 'keyword1, keyword2'} value={form.meta_keywords} onChange={e => setForm({ ...form, meta_keywords: e.target.value })} />
                  </div>
                </div>
              )}
            </div>

            {/* Delivery Areas */}
            {deliveryAreas.length > 0 && (
              <div className="pf-card">
                {sectionHeader('areas', dt('available_in_areas', lang))}
                {expandedSections.areas && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ marginBottom: 8 }}>
                      <button type="button" className="btn btn-sm btn-white" onClick={() => setForm({ ...form, delivery_area_ids: form.delivery_area_ids.length === deliveryAreas.length ? [] : deliveryAreas.map((a: any) => a.id) })}>
                        {form.delivery_area_ids.length === deliveryAreas.length ? dt('deselect_all', lang) : dt('select_all', lang)}
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--gray-400)', marginInlineStart: 8 }}>({dt('all_areas_default', lang)})</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {deliveryAreas.map((area: any) => (
                        <label key={area.id} className={`pf-area-chip ${form.delivery_area_ids.includes(area.id) ? 'pf-area-chip-active' : ''}`}>
                          <input type="checkbox" checked={form.delivery_area_ids.includes(area.id)} onChange={() => setForm({ ...form, delivery_area_ids: toggleMulti(form.delivery_area_ids, area.id) })} style={{ display: 'none' }} />
                          {lang === 'ar' ? area.name : (area.name_en || area.name)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
        </form>
      </div>
    );
  }

  // ==================== LIST VIEW ====================
  return (
    <div>
      <div className="page-header">
        <h1>{dt('products', lang)}</h1>
        <button className="btn btn-primary" onClick={() => openForm()}><FiPlus /> {dt('add_product', lang)}</button>
      </div>

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <FiSearch style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineStart: 12, color: 'var(--gray-400)' }} />
          <input
            className="form-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={lang === 'ar' ? 'بحث بالاسم أو SKU...' : 'Search by name or SKU...'}
            style={{ paddingInlineStart: 36 }}
          />
        </div>
        <select className="form-input" style={{ width: 'auto' }} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="">{lang === 'ar' ? 'كل الفئات' : 'All Categories'}</option>
          {categories.map((c: any) => (
            <option key={c.id} value={c.id}>{c.name}{c.name_ar ? ` — ${c.name_ar}` : ''}</option>
          ))}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">{lang === 'ar' ? 'كل الحالات' : 'All Status'}</option>
          <option value="active">{dt('active', lang)}</option>
          <option value="inactive">{dt('inactive', lang)}</option>
        </select>
      </div>

      {loading && products.length === 0 ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <FiPackage />
          <h3>{dt('no_products', lang)}</h3>
          <p>{dt('add_first_product', lang)}</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => openForm()}><FiPlus /> {dt('add_product', lang)}</button>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{dt('image', lang)}</th>
                  <th>{dt('name', lang)}</th>
                  <th>{dt('category', lang)}</th>
                  <th>{dt('price', lang)}</th>
                  <th>{dt('sku', lang)}</th>
                  <th>{dt('featured', lang)}</th>
                  <th>{dt('status', lang)}</th>
                  <th>{dt('actions', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      {p.image ? (
                        <img src={`${STORAGE_URL}/${p.image}`} alt="" style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 50, height: 50, borderRadius: 8, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiPackage /></div>
                      )}
                    </td>
                    <td>
                      <strong>{p.name}</strong>
                      {p.name_ar && <div style={{ fontSize: 12, color: 'var(--gray-400)', direction: 'rtl' }}>{p.name_ar}</div>}
                    </td>
                    <td>{p.category?.name}</td>
                    <td>
                      <strong>{store.currency_symbol} {Number(p.price).toFixed(3)}</strong>
                      {p.compare_price && <span style={{ textDecoration: 'line-through', color: 'var(--gray-400)', marginInlineStart: 8, fontSize: 12 }}>{store.currency_symbol} {Number(p.compare_price).toFixed(3)}</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{p.sku || '-'}</td>
                    <td>{p.is_featured ? '★' : '-'}</td>
                    <td><span className={`badge ${p.is_active ? 'badge-ready' : 'badge-cancelled'}`}>{p.is_active ? dt('active', lang) : dt('inactive', lang)}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-sm btn-white" onClick={() => openForm(p)}><FiEdit2 /></button>
                        <button className="btn btn-sm btn-white" onClick={() => handleDelete(p.id)} style={{ color: 'var(--danger)' }}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {prodPage < prodLastPage && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn btn-white" onClick={() => setProdPage(prodPage + 1)} disabled={loading}>
                {loading ? '...' : dt('load_more', lang)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
